import React, { useEffect, useRef, useState } from 'react';
import { Palette, Plus, X, Type, Sparkles, Check } from 'lucide-react';
import { PRESET_PALETTES, PALETTE_LABELS, FONT_PAIRS, ALL_FONTS, loadGoogleFonts, readableOn } from './brandData';
import { colorName } from './colorNames';

function Swatch({ c, i, onEdit, onRemove }) {
  const ref = useRef(null);
  return (
    <div className="group relative flex flex-col items-center gap-1" data-testid={`palette-swatch-${i}`}>
      <button type="button" onClick={() => ref.current?.click()} title="Click to change"
        className="w-14 h-14 rounded-xl border border-black/10 shadow-sm hover:scale-105 transition-transform" style={{ background: c.hex }} />
      <input ref={ref} type="color" value={c.hex} onChange={(e) => onEdit({ ...c, hex: e.target.value })} className="sr-only" />
      <span className="text-[10px] font-semibold text-[#0A0F1C] max-w-[72px] text-center leading-tight" data-testid={`palette-name-${i}`}>{colorName(c.hex)}</span>
      <span className="text-[10px] font-mono text-[#475569] uppercase">{c.hex}</span>
      <span className="text-[10px] font-semibold text-[#94A3B8]">{c.label || PALETTE_LABELS[i] || ''}</span>
      <button type="button" onClick={onRemove} data-testid={`palette-remove-${i}`}
        className="absolute -top-1.5 -right-1.5 bg-white border border-[#E2E8F0] text-[#64748B] hover:text-red-500 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"><X size={11} /></button>
    </div>
  );
}

export default function BrandPicker({ vision, onSave }) {
  const logoColors = [...new Set((vision.assets || []).filter((a) => a.kind === 'logo').flatMap((a) => a.colors || []))];
  const [brand, setBrand] = useState(vision.brand || { palette: [], heading_font: '', body_font: '' });
  const timer = useRef(null);
  const serverBrand = JSON.stringify(vision.brand || {});
  useEffect(() => { if (!timer.current) setBrand(JSON.parse(serverBrand)); }, [serverBrand]);
  useEffect(() => { loadGoogleFonts(); return () => clearTimeout(timer.current); }, []);
  useEffect(() => { loadGoogleFonts([brand.heading_font, brand.body_font].filter((f) => f && !ALL_FONTS.includes(f))); }, [brand.heading_font, brand.body_font]);
  const palette = brand.palette || [];

  const commit = (next) => {
    setBrand(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { timer.current = null; onSave({ brand: next }); }, 400);
  };
  const setPalette = (pal) => commit({ ...brand, palette: pal.slice(0, 8).map((c, i) => ({ hex: c.hex, label: c.label || PALETTE_LABELS[i] || '' })) });
  const addColor = (hex) => { if (!palette.some((c) => c.hex === hex)) setPalette([...palette, { hex, label: PALETTE_LABELS[palette.length] }]); };
  const applyPreset = (colors) => setPalette(colors.map((hex, i) => ({ hex, label: PALETTE_LABELS[i] })));
  const pickPair = (pr) => commit({ ...brand, heading_font: pr.heading, body_font: pr.body });

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 mt-4" data-testid="brand-picker">
      <div className="flex items-center gap-2 mb-3"><Palette size={15} className="text-[#1D4ED8]" /><h4 className="font-bold text-sm text-[#0A0F1C]">Brand colors & fonts</h4></div>

      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-5">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wide text-[#64748B] mb-2">Your palette</label>
          <div className="flex flex-wrap gap-3 items-start" data-testid="palette-list">
            {palette.map((c, i) => (
              <Swatch key={i} c={c} i={i} onEdit={(nc) => setPalette(palette.map((x, j) => (j === i ? nc : x)))} onRemove={() => setPalette(palette.filter((_, j) => j !== i))} />
            ))}
            {palette.length < 8 && (
              <label className="w-14 h-14 rounded-xl border-2 border-dashed border-[#CBD5E1] flex items-center justify-center text-[#94A3B8] hover:border-[#1D4ED8] hover:text-[#1D4ED8] cursor-pointer" data-testid="palette-add-custom" title="Add a custom color">
                <Plus size={18} />
                <input type="color" className="sr-only" onChange={(e) => addColor(e.target.value)} />
              </label>
            )}
          </div>

          {logoColors.length > 0 && (
            <div className="mt-4" data-testid="logo-colors">
              <div className="text-xs font-semibold text-[#64748B] mb-1.5 flex items-center gap-1"><Sparkles size={12} className="text-[#F97316]" /> Colors found in your logo — click to add</div>
              <div className="flex flex-wrap gap-2">
                {logoColors.map((hex) => {
                  const on = palette.some((c) => c.hex === hex);
                  return (
                    <button key={hex} type="button" onClick={() => addColor(hex)} data-testid={`logo-color-${hex.slice(1)}`} title={hex}
                      className={`inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border transition-colors ${on ? 'border-[#1D4ED8] bg-[#EFF6FF]' : 'border-[#E2E8F0] hover:border-[#1D4ED8]'}`}>
                      <span className="w-7 h-7 rounded-full border border-black/10 flex items-center justify-center" style={{ background: hex }}>
                        {on && <Check size={13} style={{ color: readableOn(hex) }} />}
                      </span>
                      <span className="text-left leading-tight">
                        <span className="block text-xs font-bold text-[#0A0F1C]" data-testid={`logo-color-name-${hex.slice(1)}`}>{colorName(hex)}</span>
                        <span className="block text-[10px] font-mono text-[#94A3B8] uppercase">{hex}</span>
                      </span>
                    </button>
                  );
                })}
                <button type="button" onClick={() => applyPreset(logoColors.slice(0, 5))} data-testid="logo-colors-use-all" className="text-xs font-bold text-[#1D4ED8] hover:underline ml-1">Use all</button>
              </div>
            </div>
          )}

          <div className="mt-4">
            <div className="text-xs font-semibold text-[#64748B] mb-1.5">Or start from a ready-made palette</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="preset-palettes">
              {PRESET_PALETTES.map((p) => (
                <button key={p.name} type="button" onClick={() => applyPreset(p.colors)} data-testid={`preset-${p.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                  className="text-left rounded-lg border border-[#E2E8F0] p-1.5 hover:border-[#1D4ED8] transition-colors">
                  <div className="flex h-6 rounded overflow-hidden">{p.colors.map((c) => <span key={c} className="flex-1" style={{ background: c }} />)}</div>
                  <div className="text-[10px] font-semibold text-[#475569] mt-1 truncate">{p.name}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wide text-[#64748B] mb-2 flex items-center gap-1"><Type size={12} /> Font pairing</label>
          <div className="grid grid-cols-2 gap-2 max-h-[260px] overflow-y-auto pr-1" data-testid="font-pairs">
            {FONT_PAIRS.map((pr) => {
              const on = brand.heading_font === pr.heading && brand.body_font === pr.body;
              return (
                <button key={pr.name} type="button" onClick={() => pickPair(pr)} data-testid={`font-pair-${pr.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                  className={`text-left rounded-lg border p-2.5 transition-colors ${on ? 'border-[#1D4ED8] bg-[#EFF6FF]' : 'border-[#E2E8F0] hover:border-[#1D4ED8]'}`}>
                  <div className="text-lg leading-tight text-[#0A0F1C] truncate" style={{ fontFamily: pr.heading, fontWeight: 700 }}>{pr.heading}</div>
                  <div className="text-xs text-[#475569] truncate" style={{ fontFamily: pr.body }}>{pr.body} — body text</div>
                  <div className="text-[10px] font-bold uppercase tracking-wide text-[#94A3B8] mt-1">{pr.name}</div>
                </button>
              );
            })}
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3">
            {[['heading_font', 'Heading font'], ['body_font', 'Body font']].map(([k, lbl]) => (
              <label key={k} className="text-xs font-semibold text-[#64748B]">{lbl}
                <select value={brand[k] || ''} onChange={(e) => commit({ ...brand, [k]: e.target.value })} data-testid={`select-${k}`}
                  className="mt-1 w-full px-2 py-1.5 rounded-lg border border-[#E2E8F0] text-sm text-[#0A0F1C] bg-white" style={{ fontFamily: brand[k] || undefined }}>
                  <option value="">Not sure yet</option>
                  {(brand[k] && !ALL_FONTS.includes(brand[k]) ? [brand[k], ...ALL_FONTS] : ALL_FONTS).map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
                </select>
              </label>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
}
